import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, FormControl, InputLabel, Select, MenuItem, Box, Typography,
  Chip, InputAdornment, IconButton, Divider, Alert, Paper, CircularProgress, Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { fileService, partService, truckService } from '../api/api';
import { extractInvoiceFilename, invoiceDisplayName, parseInvoiceUrls, serializeInvoiceUrls } from './truckFormConfig';
import { currentPartPlacement, partBayLocation } from '../utils/partLocation.mjs';

const inventoryStorageLocations = ['Shelf', 'Solvent Shed', 'Shop'];
const storageTransferOptions = [
  { value: 'shopToSolventShed', label: 'Shop → Solvent Shed', from: 'Shop', to: 'Solvent Shed' },
  { value: 'solventShedToShop', label: 'Solvent Shed → Shop', from: 'Solvent Shed', to: 'Shop' },
];

function assignmentValue(truckId, location, fallback = '') {
  if (truckId) return `truck:${truckId}`;
  if (location) return `location:${location}`;
  return fallback;
}

export default function EditPartDialog({ open, onClose, part, onPartUpdated, hideVehicleAssignment = false }) {
  const [formData, setFormData] = useState({});
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [invoicePreviews, setInvoicePreviews] = useState([]);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [partPhotos, setPartPhotos] = useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [activePartId, setActivePartId] = useState(null);
  const [storageTransfer, setStorageTransfer] = useState('');
  const [storageBayLocation, setStorageBayLocation] = useState('');

  useEffect(() => {
    if (open && part) {
      const existingTransfer = storageTransferOptions.find((option) => (
        option.from === part.unit_came_off_from_location
        && option.to === part.unit_intended_for_location
        && !part.unit_came_off_from
        && !part.unit_intended_for
      ));
      fetchTrucks();
      setActivePartId(part.id);
      setFormData({
        sku: part.sku || '',
        make: part.make || '',
        model: part.model || '',
        year: part.year || new Date().getFullYear(),
        part_type: part.part_type || '',
        part_number_oem: part.part_number_oem || '',
        biomed_part_number_sku: part.biomed_part_number_sku || '',
        serial_number: part.serial_number || '',
        cross_reference: part.cross_reference || '',
        quantity: part.quantity ?? 1,
        unit_came_off_from: part.unit_came_off_from || '',
        unit_came_off_from_location: part.unit_came_off_from_location || (!part.unit_came_off_from ? currentPartPlacement(part).storageArea || '' : ''),
        unit_intended_for: part.unit_intended_for || '',
        unit_intended_for_location: part.unit_intended_for_location || '',
        used_new: part.used_new !== undefined ? part.used_new : true,
        condition_scale: part.condition_scale || 5,
        notes: part.notes || '',
        condition_notes: part.condition_notes || '',
        condition_of_storage: part.condition_of_storage || '',
        recommended_storage: part.recommended_storage || '',
        location: partBayLocation(part),
        date_purchased: part.date_purchased ? part.date_purchased.split('T')[0] : '',
        purchase_price: part.purchase_price || '',
        current_value: part.current_value || '',
        receipt_number: part.receipt_number || '',
        photo_url: part.photo_url || '',
        invoice_url: part.invoice_url || ''
      });
      setError('');
      setInvoiceFiles([]);
      setInvoicePreviews([]);
      setExistingInvoices(parseInvoiceUrls(part.invoice_url));
      setPhotoFiles([]);
      setPhotoPreviews([]);
      setExistingPhoto(part.photo_url || null);
      setSelectedPhotoIds([]);
      setStorageTransfer(existingTransfer?.value || '');
      setStorageBayLocation(existingTransfer ? partBayLocation(part) : '');
      fetchPartPhotos(part.id, part.photo_url || null);
    }
  }, [open, part]);

  async function fetchPartPhotos(partId, fallbackPhoto = null) {
    try {
      const res = await partService.getPhotos(partId);
      const photos = Array.isArray(res.data) ? res.data : [];
      const hasFallback = fallbackPhoto && !photos.some((photo) => photo.url === fallbackPhoto);
      setPartPhotos(hasFallback ? [{ id: null, url: fallbackPhoto, original_name: 'Current photo' }, ...photos] : photos);
      setSelectedPhotoIds([]);
    } catch (err) {
      console.error('Error fetching part photos:', err);
      setPartPhotos(fallbackPhoto ? [{ id: null, url: fallbackPhoto, original_name: 'Current photo' }] : []);
      setSelectedPhotoIds([]);
    }
  }

  async function fetchTrucks() {
    try {
      const res = await truckService.getAll();
      setTrucks(res.data || []);
    } catch (err) {
      console.error('Error fetching trucks:', err);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'location') setStorageBayLocation(value);
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleAssignmentChange = (side, value) => {
    setStorageTransfer('');
    setStorageBayLocation('');
    const isTruck = String(value).startsWith('truck:');
    const isLocation = String(value).startsWith('location:');
    const location = isLocation ? String(value).slice(9) : '';
    const truckId = isTruck ? String(value).slice(6) : '';
    setFormData((current) => ({
      ...current,
      [`unit_${side}`]: truckId,
      [`unit_${side}_location`]: location,
    }));
  };

  const handleStorageTransferChange = (value) => {
    setStorageTransfer(value);
    setStorageBayLocation(formData.location || '');
    setError('');
    const transfer = storageTransferOptions.find((option) => option.value === value);
    if (!transfer) {
      setFormData((current) => {
        const currentArea = currentPartPlacement(current).storageArea;
        return {
          ...current,
          unit_came_off_from: '',
          unit_came_off_from_location: currentArea,
          unit_intended_for: '',
          unit_intended_for_location: '',
        };
      });
      return;
    }

    setFormData((current) => ({
      ...current,
      unit_came_off_from: '',
      unit_came_off_from_location: transfer.from,
      unit_intended_for: '',
      unit_intended_for_location: transfer.to,
    }));
  };

  const handleStorageBayLocationChange = (value) => {
    setStorageBayLocation(value);
    const transfer = storageTransferOptions.find((option) => option.value === storageTransfer);
    if (!transfer) return;
    setFormData((current) => ({ ...current, location: value }));
  };

  const getTruckLabel = (truck) => [
    truck.name || `Vehicle ${truck.id}`,
    truck.license_plate ? `Plate: ${truck.license_plate}` : null,
    [truck.year, truck.make, truck.model].filter(Boolean).join(' '),
  ].filter(Boolean).join(' | ');

  const handleInvoiceUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (files.some((file) => !allowedTypes.includes(file.type))) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and PDF are allowed.');
      return;
    }

    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setInvoiceFiles((prev) => [...prev, ...files]);
    setInvoicePreviews((prev) => [...prev, ...files.map((file) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : 'pdf'))]);
    e.target.value = '';
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFile = files.find((file) => !allowedTypes.includes(file.type));
    if (invalidFile) {
      setError('Invalid image type. Only JPEG, PNG, GIF, and WEBP are allowed.');
      e.target.value = '';
      return;
    }

    const oversizedFile = files.find((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFile) {
      setError('One or more photos are too large. Maximum size is 10MB each.');
      e.target.value = '';
      return;
    }

    setPhotoFiles((prev) => [...prev, ...files].slice(0, 35));
    setPhotoPreviews((prev) => [
      ...prev,
      ...files.map((file) => URL.createObjectURL(file)),
    ].slice(0, 35));
    e.target.value = '';
  };

  const removeSelectedPhoto = (index) => {
    setPhotoFiles((prev) => prev.filter((_, idx) => idx !== index));
    setPhotoPreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const deleteExistingPhoto = async (photo) => {
    const partId = activePartId || part?.id;
    if (!partId) {
      setError('Part is still loading. Please close and reopen this part, then try again.');
      return;
    }

    try {
      if (photo.id) {
        await partService.deletePhoto(partId, photo.id);
      } else if (photo.url) {
        const filename = extractInvoiceFilename(photo.url);
        if (filename) {
          await fetch(`/api/upload/photo/part/${encodeURIComponent(filename)}`, { method: 'DELETE' });
        }
        await partService.update(partId, { photo_url: null });
      }

      const refreshed = await partService.getById(partId);
      const nextPhotoUrl = refreshed.data?.photo_url || null;
      setExistingPhoto(nextPhotoUrl);
      setFormData((prev) => ({ ...prev, photo_url: nextPhotoUrl || '' }));
      await fetchPartPhotos(partId, nextPhotoUrl);
      if (onPartUpdated && refreshed.data) onPartUpdated(refreshed.data);
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (err) {
      console.error('Error deleting part photo:', err);
      setError('Failed to delete photo. Please try again.');
    }
  };

  const togglePhotoSelection = (photoId) => {
    if (!photoId) return;
    setSelectedPhotoIds((prev) => (
      prev.includes(photoId)
        ? prev.filter((id) => id !== photoId)
        : [...prev, photoId]
    ));
  };

  const deleteSelectedPhotos = async (photoIds = selectedPhotoIds) => {
    const partId = activePartId || part?.id;
    const idsToDelete = Array.from(new Set((photoIds || []).filter(Boolean)));
    if (!idsToDelete.length) return;
    if (!partId) {
      setError('Part is still loading. Please close and reopen this part, then try again.');
      return;
    }
    const confirmed = window.confirm(`Delete ${idsToDelete.length} selected photo${idsToDelete.length === 1 ? '' : 's'}?`);
    if (!confirmed) return;

    try {
      await partService.deletePhotos(partId, idsToDelete);
      const refreshed = await partService.getById(partId);
      const nextPhotoUrl = refreshed.data?.photo_url || null;
      setExistingPhoto(nextPhotoUrl);
      setFormData((prev) => ({ ...prev, photo_url: nextPhotoUrl || '' }));
      setSelectedPhotoIds([]);
      await fetchPartPhotos(partId, nextPhotoUrl);
      if (onPartUpdated && refreshed.data) onPartUpdated(refreshed.data);
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (err) {
      console.error('Error deleting selected part photos:', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to delete selected photos. Please try again.';
      setError(message);
    }
  };

  const removeExistingInvoice = async (invoiceUrl) => {
    const filename = extractInvoiceFilename(invoiceUrl);
    if (filename) {
      try {
        await fileService.deleteInvoice(filename);
      } catch (err) {
        console.error('Error deleting invoice:', err);
      }
    }
    setExistingInvoices((prev) => prev.filter((url) => url !== invoiceUrl));
  };

  const removePendingInvoice = (index) => {
    setInvoicePreviews((prev) => {
      const preview = prev[index];
      if (preview && preview !== 'pdf') {
        try { URL.revokeObjectURL(preview); } catch { /* noop */ }
      }
      return prev.filter((_, i) => i !== index);
    });
    setInvoiceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const partId = activePartId || part?.id;
    if (!partId) {
      setError('Part is still loading. Please close and reopen this part, then try again.');
      return;
    }

    if (!formData.sku || !formData.part_type || !formData.location) {
      setError('Please fill in required fields: SKU, Part Type, and Location');
      return;
    }
    if (storageTransfer && !storageBayLocation.trim()) {
      setError('Please enter the exact destination location, for example Bay-01-Z083.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let photoUrl = partPhotos[0]?.url || existingPhoto || null;
      const invoiceUrls = [...existingInvoices];

      if (invoiceFiles.length) {
        setUploading(true);
        for (const invoiceFile of invoiceFiles) {
          const uploadRes = await fileService.uploadInvoice(invoiceFile);
          if (uploadRes.data.invoicePath) invoiceUrls.push(uploadRes.data.invoicePath);
        }
        setUploading(false);
      }

      const partData = {
        ...formData,
        year: parseInt(formData.year) || new Date().getFullYear(),
        quantity: Math.max(0, parseInt(formData.quantity, 10) || 0),
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        current_value: formData.current_value ? parseFloat(formData.current_value) : null,
        date_purchased: formData.date_purchased || null,
        unit_came_off_from: formData.unit_came_off_from ? parseInt(formData.unit_came_off_from) : null,
        unit_came_off_from_location: formData.unit_came_off_from_location || null,
        unit_intended_for: formData.unit_intended_for ? parseInt(formData.unit_intended_for) : null,
        unit_intended_for_location: formData.unit_intended_for_location || null,
        photo_url: photoUrl,
        invoice_url: serializeInvoiceUrls(invoiceUrls),
      };

      const response = await partService.update(partId, partData);
      let finalPart = response.data;

      if (photoFiles.length) {
        setPhotoUploading(true);
        await partService.uploadPhotos(partId, photoFiles);
        const refreshed = await partService.getById(partId);
        finalPart = refreshed.data;
        setPhotoFiles([]);
        setPhotoPreviews([]);
      }
      
      if (onPartUpdated) {
        onPartUpdated(finalPart);
      }
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      onClose();
    } catch (err) {
      console.error('Error updating part:', err);
      setError('Failed to update part. Please try again.');
      setUploading(false);
      setPhotoUploading(false);
    } finally {
      setLoading(false);
      setPhotoUploading(false);
    }
  };

  const conditionLevels = [
    { value: 1, label: 'Poor', color: '#d32f2f', bgColor: '#ffebee' },
    { value: 2, label: 'Fair', color: '#f57c00', bgColor: '#fff3e0' },
    { value: 3, label: 'Good', color: '#fbc02d', bgColor: '#fffde7' },
    { value: 4, label: 'Very Good', color: '#689f38', bgColor: '#f1f8e9' },
    { value: 5, label: 'Excellent', color: '#2e7d32', bgColor: '#e8f5e9' },
  ];

  const currentCondition = conditionLevels.find(c => c.value === formData.condition_scale) || conditionLevels[4];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BuildIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Edit Part</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
          Basic Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="SKU *"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Part Type *"
              name="part_type"
              value={formData.part_type}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Location *"
              name="location"
              value={formData.location}
              onChange={handleChange}
              size="small"
              placeholder="e.g., B20-32-07-B1"
              helperText="Exact bay or bin within the selected storage area."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Make"
              name="make"
              value={formData.make}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Year"
              name="year"
              type="number"
              value={formData.year}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity ?? 0}
              onChange={handleChange}
              size="small"
              inputProps={{ min: 0, step: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Inventory2Icon fontSize="small" /></InputAdornment>,
              }}
            />
          </Grid>
          {(partPhotos.length > 0 || photoPreviews.length > 0) && (
            <Grid item xs={12}>
              <Paper sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #bbdefb',
                backgroundColor: '#f8fbff',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap'
              }}>
                {[...partPhotos, ...photoPreviews.map((url, idx) => ({ id: `new-${idx}`, url, isNew: true }))].slice(0, 6).map((photo, index) => (
                  <Box
                    key={`${photo.id || photo.url}-${index}`}
                    component="img"
                    src={photo.url}
                    alt={`Part preview ${index + 1}`}
                    onClick={() => window.open(photo.url, '_blank')}
                    sx={{
                      width: 96,
                      height: 72,
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      borderRadius: 1.5,
                      border: '1px solid #d7e3f4',
                      cursor: 'zoom-in'
                    }}
                  />
                ))}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1565c0' }}>
                    Part Photos
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {partPhotos.length} saved photo{partPhotos.length === 1 ? '' : 's'}
                    {photoPreviews.length ? ` + ${photoPreviews.length} pending upload${photoPreviews.length === 1 ? '' : 's'}` : ''}. Use the photo section below to add or remove photos.
                  </Typography>
                </Box>
                {partPhotos[0]?.url && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    href={partPhotos[0].url}
                    target="_blank"
                    sx={{ borderRadius: 2 }}
                  >
                    View
                  </Button>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
          Part Numbers & References
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="OEM Part Number"
              name="part_number_oem"
              value={formData.part_number_oem}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Biomed Part Number"
              name="biomed_part_number_sku"
              value={formData.biomed_part_number_sku}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Serial Number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cross Reference"
              name="cross_reference"
              value={formData.cross_reference}
              onChange={handleChange}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
          Condition & Status
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="used_new"
                value={formData.used_new}
                onChange={(e) => setFormData(prev => ({ ...prev, used_new: e.target.value }))}
                label="Status"
              >
                <MenuItem value={true}>
                  <Chip label="New" size="small" sx={{ bgcolor: '#c8e6c9', color: '#2e7d32' }} />
                </MenuItem>
                <MenuItem value={false}>
                  <Chip label="Used" size="small" sx={{ bgcolor: '#ffccbc', color: '#d84315' }} />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: currentCondition.bgColor,
              border: `2px solid ${currentCondition.color}`,
              textAlign: 'center'
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: currentCondition.color }}>
                {currentCondition.label}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {conditionLevels.map((level) => (
                <Box
                  key={level.value}
                  onClick={() => setFormData(prev => ({ ...prev, condition_scale: level.value }))}
                  sx={{
                    flex: '1 1 18%',
                    minWidth: 80,
                    p: 1.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: formData.condition_scale === level.value 
                      ? `3px solid ${level.color}` 
                      : '2px solid #e0e0e0',
                    bgcolor: formData.condition_scale === level.value 
                      ? level.bgColor 
                      : '#fafafa',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: level.bgColor, borderColor: level.color }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: formData.condition_scale === level.value ? level.color : '#757575' }}>
                    {level.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: formData.condition_scale === level.value ? level.color : '#9e9e9e' }}>
                    {level.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Part Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              multiline
              rows={2}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Condition Notes"
              name="condition_notes"
              value={formData.condition_notes}
              onChange={handleChange}
              multiline
              rows={2}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
          Storage Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Current Storage Condition"
              name="condition_of_storage"
              value={formData.condition_of_storage}
              onChange={handleChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Recommended Storage"
              name="recommended_storage"
              value={formData.recommended_storage}
              onChange={handleChange}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {!hideVehicleAssignment && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #bbdefb', backgroundColor: '#f8fbff' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon /> Part Movement &amp; Assignment
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Track where this part came from and where it is intended to go.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5, borderColor: '#c4b5fd', bgcolor: '#faf8ff' }}>
            <Typography sx={{ fontWeight: 900, color: '#4c1d95', mb: 0.5 }}>
              Part moved?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Record a direct inventory transfer between the Shop and Solvent Shed.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: storageTransfer ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' }, gap: 1.5, width: '100%' }}>
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Storage transfer</InputLabel>
                  <Select
                    value={storageTransfer}
                    label="Storage transfer"
                    onChange={(event) => handleStorageTransferChange(event.target.value)}
                  >
                    <MenuItem value="">Not moved between storage areas</MenuItem>
                    {storageTransferOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {storageTransfer && (
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Exact destination location"
                    value={storageBayLocation}
                    onChange={(event) => handleStorageBayLocationChange(event.target.value)}
                    placeholder="Bay-01-Z083"
                    helperText={`Exact bay or bin within ${storageTransferOptions.find((option) => option.value === storageTransfer)?.to}; saved as entered.`}
                  />
                </Box>
              )}
            </Box>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Unit Came Off From</InputLabel>
                <Select
                  value={assignmentValue(formData.unit_came_off_from, formData.unit_came_off_from_location, 'location:Shelf')}
                  onChange={(event) => handleAssignmentChange('came_off_from', event.target.value)}
                  label="Unit Came Off From"
                >
                  {inventoryStorageLocations.map((location) => (
                    <MenuItem key={location} value={`location:${location}`}>{location}</MenuItem>
                  ))}
                  {trucks.map(truck => (
                    <MenuItem key={truck.id} value={`truck:${truck.id}`}>
                      {getTruckLabel(truck)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.75, display: 'block' }}>
                Vehicle or inventory area this part came from.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Unit Intended For</InputLabel>
                <Select
                  value={assignmentValue(formData.unit_intended_for, formData.unit_intended_for_location)}
                  onChange={(event) => handleAssignmentChange('intended_for', event.target.value)}
                  label="Unit Intended For"
                >
                  <MenuItem value="">No destination vehicle</MenuItem>
                  {inventoryStorageLocations.map((location) => (
                    <MenuItem key={location} value={`location:${location}`}>{location}</MenuItem>
                  ))}
                  {trucks.map(truck => (
                    <MenuItem key={truck.id} value={`truck:${truck.id}`}>
                      {getTruckLabel(truck)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.75, display: 'block' }}>
                Vehicle or inventory area this part is intended for.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
          Financial Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <TextField
                fullWidth
                label="Date Purchased (Optional)"
                name="date_purchased"
                type="date"
                value={formData.date_purchased || ''}
                onChange={handleChange}
                size="small"
                helperText="Leave blank if the purchase date is unknown."
                InputLabelProps={{ shrink: true }}
              />
              {formData.date_purchased && (
                <Button size="small" color="inherit" onClick={() => setFormData((prev) => ({ ...prev, date_purchased: '' }))} sx={{ mt: 0.25 }}>
                  Clear date
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Purchase Price"
              name="purchase_price"
              type="number"
              value={formData.purchase_price}
              onChange={handleChange}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Current Value"
              name="current_value"
              type="number"
              value={formData.current_value}
              onChange={handleChange}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Receipt Number"
              name="receipt_number"
              value={formData.receipt_number}
              onChange={handleChange}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon /> Part Photo (Optional)
          </Typography>

          {(partPhotos.length === 0 && photoFiles.length === 0) ? (
            <Box
              component="label"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2.5, sm: 3 },
                border: '2px dashed #1976d2',
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: '#e3f2fd',
                '&:hover': { backgroundColor: '#bbdefb' }
              }}
            >
              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#1976d2', mb: 1 }} />
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                Tap to upload part photos (JPEG, PNG, GIF, WEBP — Max 10MB each)
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              backgroundColor: '#fff',
              borderRadius: 2,
              border: '1px solid #bbdefb'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
                {partPhotos.map((photo, index) => (
                  <Box key={`${photo.id || photo.url}-${index}`} sx={{ position: 'relative' }}>
                    {photo.id && (
                      <Checkbox
                        checked={selectedPhotoIds.includes(photo.id)}
                        onChange={() => togglePhotoSelection(photo.id)}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          left: -10,
                          zIndex: 2,
                          backgroundColor: 'white',
                          borderRadius: 1,
                          boxShadow: 1,
                          p: 0.25,
                          '&:hover': { backgroundColor: '#e3f2fd' },
                        }}
                      />
                    )}
                    <Box
                      component="img"
                      src={photo.url}
                      alt={`Saved part ${index + 1}`}
                      onClick={() => photo.id ? togglePhotoSelection(photo.id) : window.open(photo.url, '_blank')}
                      sx={{
                        width: { xs: 104, sm: 126 },
                        height: { xs: 84, sm: 96 },
                        objectFit: 'contain',
                        backgroundColor: '#f8fbff',
                        borderRadius: 1.5,
                        border: selectedPhotoIds.includes(photo.id) ? '3px solid #d32f2f' : '1px solid #d7e3f4',
                        cursor: photo.id ? 'pointer' : 'zoom-in',
                      }}
                    />
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        if (photo.id && selectedPhotoIds.length > 1 && selectedPhotoIds.includes(photo.id)) {
                          deleteSelectedPhotos(selectedPhotoIds);
                        } else {
                          deleteExistingPhoto(photo);
                        }
                      }}
                      color="error"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'white',
                        boxShadow: 1,
                        '&:hover': { backgroundColor: '#ffebee' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {photoPreviews.map((preview, index) => (
                  <Box key={preview} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={preview}
                      alt={`Pending part ${index + 1}`}
                      sx={{
                        width: { xs: 104, sm: 126 },
                        height: { xs: 84, sm: 96 },
                        objectFit: 'contain',
                        backgroundColor: '#fffaf0',
                        borderRadius: 1.5,
                        border: '1px solid #ffb74d',
                      }}
                    />
                    <Chip label="New" size="small" sx={{ position: 'absolute', left: 6, bottom: 6, fontWeight: 800, bgcolor: '#fff3e0', color: '#e65100' }} />
                    <IconButton
                      onClick={() => removeSelectedPhoto(index)}
                      color="error"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'white',
                        boxShadow: 1,
                        '&:hover': { backgroundColor: '#ffebee' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedPhotoIds.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={deleteSelectedPhotos}
                    sx={{ borderRadius: 2, fontWeight: 'bold' }}
                  >
                    Delete Selected ({selectedPhotoIds.length})
                  </Button>
                )}
                {partPhotos[0]?.url && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    href={partPhotos[0].url}
                    target="_blank"
                    sx={{ borderRadius: 2 }}
                  >
                    View First
                  </Button>
                )}
                {!photoUploading && (
                  <Box component="label" sx={{ cursor: 'pointer' }}>
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                    <Button variant="outlined" size="small" component="span" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2 }}>
                      Add More
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {photoUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Uploading photo…</Typography>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> Invoice Attachment
          </Typography>

          <Box
            component="label"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2.5, sm: 3 },
              border: '2px dashed #4caf50',
              borderRadius: 2,
              cursor: 'pointer',
              backgroundColor: '#f1f8e9',
              '&:hover': { backgroundColor: '#dcedc8' }
            }}
          >
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf"
              onChange={handleInvoiceUpload}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#2e7d32', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', textAlign: 'center' }}>
              Tap to add invoice attachments (JPEG, PNG, GIF, PDF — Max 10MB each)
            </Typography>
          </Box>

          {(existingInvoices.length > 0 || invoiceFiles.length > 0) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {existingInvoices.map((invoiceUrl, index) => (
                <Box key={`${invoiceUrl}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                  <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} href={invoiceUrl} target="_blank" sx={{ borderRadius: 2, fontWeight: 800 }}>
                    {invoiceDisplayName(invoiceUrl, `Invoice ${index + 1}`)}
                  </Button>
                  <IconButton onClick={() => removeExistingInvoice(invoiceUrl)} color="error" size="small" sx={{ backgroundColor: '#ffebee' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {invoiceFiles.map((file, index) => (
                <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                  {invoicePreviews[index] && invoicePreviews[index] !== 'pdf' ? (
                    <Box component="img" src={invoicePreviews[index]} alt="Invoice preview" sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />
                  ) : (
                    <ReceiptIcon sx={{ color: '#d32f2f', fontSize: 30 }} />
                  )}
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 800 }}>{file.name}</Typography>
                  <IconButton onClick={() => removePendingInvoice(index)} color="error" size="small" sx={{ backgroundColor: '#ffebee' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {uploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Uploading invoice…</Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || uploading || photoUploading}
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)' }
          }}
        >
          {photoUploading || uploading ? 'Uploading...' : loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
